import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { ethers } from "ethers";
import { useSnackbar } from "notistack";
import useSound from "use-sound";
import JSConfetti from "js-confetti";
import { Button, Stack } from "@mui/material";

import GAMES from "./games";
import NETWORKS from "./networks";
import SessionContext from "./contexts/session";
import { SocketContext } from "./contexts/socket";
import { parseAmount, truncateAddress } from "./utils";

import Layout from "./components/Layout";
import Game from "./components/routes/Game";
import Home from "./components/routes/Home";
import UserProfile from "./components/routes/UserProfile";
import { FormatInputs } from "./components/History";
import ButtonBet from "./components/routes/Game/ButtonBet";
import RoutesContext from "./contexts/routes";

import "./App.css";
import { getUserByAddress } from "./models/users";
import TermsOfService from "./components/routes/TermsOfService";
import Team from "./components/routes/NewTeam";
import Faq from "./components/routes/Faq";
import { useDetectAdBlock } from "adblock-detect-react";

import GA4React from "ga-4-react";
import privateCheckJson from "./private-check.json";

const jsConfetti = new JSConfetti();

export default function AppRoutes() {
  const [ga4, setGa4] = useState(null);
  const adBlockDetected = useDetectAdBlock();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {
    account,
    pending,
    isAwaitingWallet,
    setPending,
    animationQueue,
    setAnimationQueue,
    executing,
    setExecuting,
    volume,
    setUser,
    selectedNetwork,
  } = useContext(SessionContext);
  const socket = useContext(SocketContext);
  const [playYouWin] = useSound("/audio/win.wav", { volume });
  const [playYouLose] = useSound("/audio/error.ogg", { volume });
  const [playJackpot] = useSound("/audio/jackpot.wav", { volume });

  const location = useLocation();

  const handleSnackbarClick = useCallback(
    (key) => {
      closeSnackbar(key);
    },
    [closeSnackbar]
  );

  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (!ga4 && !adBlockDetected) {
      if (privateCheckJson["isGa4Code"]) {
        try {
          var ga4_code = require("../../private/ga4_code");
          const ga4react = new GA4React(ga4_code["ga4Code"]);
          ga4react.initialize().then((ga4) => {
            setGa4(ga4);
          });
        } catch (err) {}
      }
    }
  }, [adBlockDetected, ga4]);

  useEffect(() => {
    if (ga4) {
      ga4.pageview(location.pathname);
    }
  }, [ga4, location.pathname]);

  const executeTransactionUi = useCallback(
    (transaction) => {
      if (!transaction) {
        // for useEffect on route change, remove if the animation on change is removed!
        return;
      }
      setAnimationQueue((animationQueue) =>
        animationQueue.filter((tx) => tx._id !== transaction._id)
      );
      setExecuting();
      if (transaction.isWinner) {
        playYouWin();
        jsConfetti.addConfetti();
      } else {
        playYouLose();
      }
      const key = enqueueSnackbar(
        <Stack spacing={1}>
          You {transaction.isWinner ? "won" : "lost"}{" "}
          {ethers.utils.formatEther(transaction.betValue)}{" "}
          {NETWORKS[transaction.chainId].ui.symbol}{" "}
          {transaction.isWinner ? "🥳" : "😢"}
          <Stack direction="row" spacing={4} sx={{ alignItems: "center" }}>
            <FormatInputs transaction={transaction} />
            <FormatInputs transaction={transaction} isResult={true} />
          </Stack>
          <Button variant="contained" onClick={() => handleSnackbarClick(key)}>
            Close
          </Button>
          <ButtonBet
            betAmount={ethers.utils.formatEther(transaction.betValue)}
            modulo={Number(transaction.modulo)}
            inputs={transaction.inputs.map(Number)}
            disabled={isAwaitingWallet}
          />
        </Stack>,
        {
          variant: transaction.isWinner ? "success" : "error",
          style: { overflow: "hidden" },
          persist: true,
          // onClick: () => handleSnackbarClick(key),
        }
      );
    },
    [
      isAwaitingWallet,
      setAnimationQueue,
      setExecuting,
      enqueueSnackbar,
      handleSnackbarClick,
      playYouLose,
      playYouWin,
    ]
  );

  useEffect(() => {
    if (account) {
      enqueueSnackbar(`Logged in as ${truncateAddress(account)}`, {
        variant: "info",
      });
    }
  }, [enqueueSnackbar, account]);

  useEffect(() => {
    let didCancel = false;
    async function updateUser() {
      const user = await getUserByAddress(account);
      if (!didCancel) {
        setUser(user);
      }
    }
    if (account) {
      updateUser();
    } else {
      setUser(null);
    }
    return () => {
      didCancel = true;
    };
  }, [account, setUser]);

  useEffect(() => {
    if (socket && account) {
      function on_new_transaction(transaction) {
        // setTransactions((transactions) => [transaction, ...transactions]);
        if (transaction.fromAddress === account) {
          const pending_ = pending.find(
            (tx) => tx.placeBetHash === transaction.placeBetHash
          );
          if (pending_) {
            closeSnackbar(pending_.snackbarKey);
            setPending((pending) =>
              pending.filter(
                (tx) => tx.placeBetHash !== transaction.placeBetHash
              )
            );
          }
          setAnimationQueue((animationQueue) => [
            ...animationQueue,
            transaction,
          ]);
        }
      }
      function on_new_jackpot(bet) {
        if (account === bet.gambler) {
          jsConfetti.addConfetti({ emojis: ["🦄"] });

          playJackpot();
          const key = enqueueSnackbar(
            `You won the jackpot: ${parseAmount(
              bet.jackpotValue,
              NETWORKS[bet.chainId]
            )}! :)`,
            {
              variant: "success",
              persist: true,
              onClick: () => handleSnackbarClick(key),
            }
          );
        }
      }
      socket.on("new_transaction", on_new_transaction);
      socket.on("new_jackpot", on_new_jackpot);
      return () => {
        socket.off("new_transaction", on_new_transaction);
        socket.off("new_jackpot", on_new_jackpot);
      };
    }
  }, [
    account,
    socket,
    pending,
    closeSnackbar,
    setAnimationQueue,
    setPending,
    playJackpot,
    enqueueSnackbar,
    selectedNetwork,
    handleSnackbarClick,
  ]);

  useEffect(() => {
    if (animationQueue.length > 0 && !executing) {
      setExecuting(animationQueue[0]);
      // executeTransactionUi(animationQueue[0]);
    }
  }, [
    animationQueue,
    executing,
    enqueueSnackbar,
    playYouLose,
    playYouWin,
    setExecuting,
  ]);

  useEffect(() => {
    if (executing) {
      if (location.pathname.includes(GAMES[Number(executing.modulo)].url)) {
        // game executes it
      } else {
        // app executes it
        executeTransactionUi(executing);
      }
    }
  }, [executeTransactionUi, executing, location.pathname]);

  return (
    <RoutesContext.Provider value={{ executeTransactionUi }}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="/user/:address" element={<UserProfile />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/team" element={<Team />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" />} />
          {Object.values(GAMES).map((game) => (
            <Route
              key={game.modulo}
              path={game.url}
              element={<Game game={game} />}
            ></Route>
          ))}
        </Route>
      </Routes>
    </RoutesContext.Provider>
  );
}
